import React from 'react';
import Swal from 'sweetalert2';

interface Evolucao {
  idEvolucao: number;
  cdPaciente: number;
  nuCpf: string;
  dtHraEvolucao: string;
  tipo: string;
  idProfissional: number;
  dsEvolucao: string;
  profissional: {
    idProfissional: number;
    nmProfissional: string;
    especialidade: {
      idEspecialidade: number;
      dsEspecialidade: string;
    };
  };
  dtHraAlteracao: string;
  cdMedicoAlteracao: number | null;
  localEvolucao: string;
  maisSaude: string | null;
}

interface ModalVisualizacaoProps {
  activeTab: "meus" | "outros" | "sadt" | "receituarios" | "altas" | null;
  modalAberto: boolean;
  setModalAberto: (open: boolean) => void;
  prontuarioAtual: string;
  evolucaoEditando: Evolucao | null;
  loadingMeus: boolean;
  meusAtendimentos: Evolucao[];
  paginaAtual: number;
  itensPorPagina: number;
  totalPaginas: number;
  mudarPagina: (page: number) => void;
  prontuarioIndexAberto: number | null;
  setProntuarioIndexAberto: (value: number | null | ((prev: number | null) => number | null)) => void;
  onSelectEvolucao?: (evolucao: Evolucao) => void;
}

const ModalVisualizacao: React.FC<ModalVisualizacaoProps> = ({
  activeTab,
  modalAberto,
  setModalAberto,
  prontuarioAtual,
  evolucaoEditando,
  loadingMeus,
  meusAtendimentos,
  paginaAtual,
  itensPorPagina,
  totalPaginas,
  mudarPagina,
  prontuarioIndexAberto,
  setProntuarioIndexAberto,
  onSelectEvolucao,
}) => {
  if (!modalAberto) return null;

 const handleCopyText = async (text: string) => {
  try {
    // Método 1: Usando writeText (mais compatível)
    if (navigator.clipboard && navigator.clipboard.writeText) {
      // Remove tags HTML e pega apenas texto puro
      const plainText = text.replace(/<[^>]*>/g, '');
      await navigator.clipboard.writeText(plainText);
    } 
    // Método 2: Fallback para métodos mais antigos
    else {
      const textArea = document.createElement('textarea');
      const plainText = text.replace(/<[^>]*>/g, '');
      textArea.value = plainText;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);
      
      if (!successful) {
        throw new Error('Não foi possível copiar');
      }
    }

    Swal.fire({
      icon: "success",
      title: "Copiado!",
      text: "Texto do atendimento copiado para a área de transferência.",
      timer: 2000,
      showConfirmButton: false,
      background: '#1f2937',
      color: '#fff',
    });
  } catch (error) {
    console.error('Erro ao copiar:', error);
    Swal.fire({
      icon: "error",
      title: "Erro!",
      text: "Não foi possível copiar o texto.",
      background: '#1f2937',
      color: '#fff',
    });
  }
};

  const handleToggleProntuario = (idx: number, evolucao: Evolucao) => {
    setProntuarioIndexAberto((prev: number | null) => {
      const proximo = prev === idx ? null : idx;
      if (proximo !== null) {
        onSelectEvolucao?.(evolucao);
      }
      return proximo;
    });
  };

  const getTabIcon = (tab: string) => {
    switch (tab) {
      case 'meus': return '👨‍⚕️';
      case 'outros': return '👥';
      case 'sadt': return '🏥';
      case 'receituarios': return '📝';
      case 'altas': return '✅';
      default: return '📄';
    }
  };

  const getTabTitle = (tab: string) => {
    switch (tab) {
      case 'meus': return 'Meus Atendimentos';
      case 'outros': return 'Outros Profissionais';
      case 'sadt': return 'SADT';
      case 'receituarios': return 'Receituários';
      case 'altas': return 'Altas';
      default: return 'Visualização';
    }
  };

  if (activeTab === "meus") {
    return (
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4"
        onClick={() => setModalAberto(false)}
      >
        <div
          className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl border border-slate-200 w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden mx-2 sm:mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-800 to-gray-800 p-4 sm:p-6 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm">
                  <span className="text-sm sm:text-lg">👨‍⚕️</span>
                </div>
                <div>
                  <h2 className="text-sm sm:text-xs lg:text-sm font-bold leading-tight">
                    Meus Atendimentos
                  </h2>
                  <p className="text-blue-100 sm:text-sm lg:text-sm mt-0.5 sm:mt-1">
                    Visualize e gerencie seus registros
                  </p>
                </div>
              </div>
              <button
                onClick={() => setModalAberto(false)}
                className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:scale-110 text-sm sm:text-base"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6 space-y-3 sm:space-y-4">
            {loadingMeus ? (
              <div className="flex flex-col items-center justify-center py-8 sm:py-12">
                <div className="w-10 h-10 sm:w-12 sm:h-12 border-3 sm:border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-3 sm:mb-4"></div>
                <p className="text-slate-600 font-medium text-sm sm:text-base">
                  Carregando atendimentos...
                </p>
              </div>
            ) : meusAtendimentos.length > 0 ? (
              meusAtendimentos
                .slice(
                  (paginaAtual - 1) * itensPorPagina,
                  paginaAtual * itensPorPagina
                )
                .map((evolucao, idxGlobal) => {
                  const idx = (paginaAtual - 1) * itensPorPagina + idxGlobal;
                  const isAberto = prontuarioIndexAberto === idx;

                  const dataAtendimento = new Date(evolucao.dtHraEvolucao);
                  const hoje = new Date();

                  const dataAtendimentoSemHora = new Date(
                    dataAtendimento.getFullYear(),
                    dataAtendimento.getMonth(),
                    dataAtendimento.getDate()
                  );

                  const hojeSemHora = new Date(
                    hoje.getFullYear(),
                    hoje.getMonth(),
                    hoje.getDate()
                  );

                  const isDataHoje = dataAtendimentoSemHora.getTime() === hojeSemHora.getTime();

                  return (
                    <div
                      key={idx}
                      className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
                    >
                      <div
                        className="cursor-pointer p-3 sm:p-4 lg:p-5 hover:bg-slate-50 transition-all duration-200 flex justify-between items-center"
                        onClick={() => handleToggleProntuario(idx, evolucao)}
                      >
                        <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                          <div className={`w-2 h-2 sm:w-3 sm:h-3 rounded-full ${isAberto ? 'bg-green-500' : 'bg-blue-500'}`}></div>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:gap-4 lg:gap-6">
                            <span className="font-semibold text-slate-800 text-sm sm:text-base">
                              {dataAtendimento.toLocaleDateString("pt-BR", {
                                day: "2-digit",
                                month: "2-digit",
                                year: "numeric",
                              })}
                            </span>
                            {isDataHoje && (
                              <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 bg-green-100 text-green-700 text-xs rounded-full font-medium mt-1 sm:mt-0">
                                Hoje
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 sm:gap-3">
                          <span className="text-slate-500 text-xs sm:text-sm hidden xs:block">
                            {isAberto ? 'Recolher' : 'Expandir'}
                          </span>
                          <span className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 flex items-center justify-center rounded-lg transition-all duration-200 text-sm sm:text-base ${
                            isAberto ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {isAberto ? "−" : "+"}
                          </span>
                        </div>
                      </div>
                      
                      {isAberto && (
                        <div className="border-t border-slate-100 p-3 sm:p-4 lg:p-5 bg-slate-50 space-y-3 sm:space-y-4">
                          <div className="flex justify-end">
                            <button
                              onClick={() => handleCopyText(evolucao.dsEvolucao || "")}
                              className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm rounded-lg sm:rounded-xl transition-all duration-200 flex items-center gap-1.5 sm:gap-2 font-medium shadow-sm hover:shadow-md"
                            >
                              <span className="text-xs sm:text-sm">📋</span>
                              Copiar Texto
                            </button>
                          </div>

                          <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-5 border border-slate-200 shadow-inner">
                            <div
                              className="whitespace-pre-wrap text-slate-700 leading-relaxed text-sm sm:text-base"
                              dangerouslySetInnerHTML={{
                                __html: evolucao.dsEvolucao || "",
                              }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
            ) : (
              <div className="text-center py-8 sm:py-12">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-slate-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
                  <span className="text-xl sm:text-2xl">📝</span>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-600 mb-1 sm:mb-2">
                  Nenhum atendimento encontrado
                </h3>
                <p className="text-slate-500 text-sm sm:text-base">
                  Não há registros de atendimento para exibir.
                </p>
              </div>
            )}
          </div>

          {/* Paginação */}
          {meusAtendimentos.length > itensPorPagina && (
            <div className="border-t border-slate-200 bg-slate-50 p-3 sm:p-4">
              <div className="flex justify-center items-center gap-1 sm:gap-2">
                <button
                  onClick={() => mudarPagina(paginaAtual - 1)}
                  disabled={paginaAtual === 1}
                  className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 flex items-center justify-center bg-white border border-slate-300 rounded-lg sm:rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200 shadow-sm text-sm"
                >
                  ←
                </button>

                {[...Array(totalPaginas)].map((_, i) => (
                  <button
                    key={i}
                    onClick={() => mudarPagina(i + 1)}
                    className={`w-8 h-8 sm:w-9 sm:h-9 xs:w-10 lg:h-10 flex items-center justify-center rounded-lg sm:rounded-xl font-medium transition-all duration-200 text-sm ${
                      paginaAtual === i + 1
                        ? "bg-blue-600 text-white shadow-md"
                        : "bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 shadow-sm"
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}

                <button
                  onClick={() => mudarPagina(paginaAtual + 1)}
                  disabled={paginaAtual === totalPaginas}
                  className="w-8 h-8 sm:w-9 sm:h-9 lg:w-10 lg:h-10 flex items-center justify-center bg-white border border-slate-300 rounded-lg sm:rounded-xl disabled:opacity-30 disabled:cursor-not-allowed hover:bg-slate-50 transition-all duration-200 shadow-sm text-sm"
                >
                  →
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Modal para outras abas (outros, sadt, receituarios, altas)
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-2 sm:p-4">
      <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl sm:rounded-2xl lg:rounded-3xl shadow-2xl border border-slate-200 w-full max-w-6xl max-h-[95vh] flex flex-col overflow-hidden mx-2 sm:mx-4">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-4 sm:p-6 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-white/20 rounded-lg sm:rounded-xl flex items-center justify-center backdrop-blur-sm">
                <span className="text-sm sm:text-lg">{getTabIcon(activeTab || '')}</span>
              </div>
              <div>
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold leading-tight">
                  {getTabTitle(activeTab || '')}
                </h2>
                <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1">
                  Visualização detalhada do registro
                </p>
              </div>
            </div>
            <button
              onClick={() => setModalAberto(false)}
              className="w-7 h-7 sm:w-8 sm:h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all duration-200 backdrop-blur-sm hover:scale-110 text-sm sm:text-base"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Informações do profissional */}
        {evolucaoEditando && (
          <div className="border-b border-slate-200 bg-white/80 backdrop-blur-sm p-3 sm:p-4 lg:p-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 text-xs sm:text-sm">
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-blue-600 text-xs sm:text-sm">📅</span>
                </div>
                <div className="min-w-0">
                  <p className="text-slate-500 text-xs">Data do Atendimento</p>
                  <p className="font-semibold text-slate-800 truncate">
                    {new Date(evolucaoEditando.dtHraEvolucao).toLocaleDateString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                    })}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 sm:gap-3">
                <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-green-600 text-xs sm:text-sm">👨‍⚕️</span>
                </div>
                <div className="min-w-0">
                  <p className="text-slate-500 text-xs">Profissional</p>
                  <p className="font-semibold text-slate-800 truncate">
                    {evolucaoEditando.profissional?.nmProfissional || "Desconhecido"}
                  </p>
                </div>
              </div>

              {evolucaoEditando.profissional?.especialidade?.dsEspecialidade && (
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-purple-600 text-xs sm:text-sm">🎯</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-slate-500 text-xs">Especialidade</p>
                    <p className="font-semibold text-slate-800 truncate">
                      {evolucaoEditando.profissional.especialidade.dsEspecialidade}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Área do prontuário */}
        <div className="flex-1 overflow-y-auto p-3 sm:p-4 lg:p-6">
          <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl border border-slate-200 shadow-inner p-3 sm:p-4 lg:p-5 lg:p-6 min-h-[300px] sm:min-h-[400px]">
            <div
              className="whitespace-pre-wrap text-slate-700 leading-relaxed text-sm sm:text-base lg:text-[15px]"
              dangerouslySetInnerHTML={{ 
                __html: prontuarioAtual || 
                '<div class="text-center py-8 sm:py-12 text-slate-500"><span class="text-2xl sm:text-3xl mb-3 sm:mb-4 block">📄</span><p class="text-sm sm:text-base">Nenhum conteúdo disponível para visualização.</p></div>' 
              }}
            />
          </div>
        </div>

        {/* Rodapé com ações - AGORA FUNCIONA PARA TODAS AS ABAS */}
        <div className="border-t border-slate-200 bg-slate-50 p-3 sm:p-4 lg:p-5">
          <div className="flex flex-col xs:flex-row justify-between items-center gap-2 sm:gap-3">      
            <button
              onClick={() => setModalAberto(false)}
              className="w-full xs:w-auto px-4 py-2 sm:px-5 sm:py-2.5 lg:px-6 lg:py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg sm:rounded-xl transition-all duration-200 font-medium shadow-sm hover:shadow-md text-xs sm:text-sm"
            >
              Fechar Visualização
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModalVisualizacao;
